import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import './OnboardingReport.css';
import { 
  Download, 
  Printer, 
  RotateCw, 
  Search, 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  TrendingUp, 
  Building2, 
  Calendar,
  Layers,
  Users,
  Briefcase,
  ShieldCheck,
  Hourglass,
  Sparkles,
  Rocket,
  ArrowUpRight,
  UserCheck,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const STATUS_CLASS = {
  "Started": "st-started",
  "Received Start Date": "st-startdate",
  "BGC Completed": "st-done",
  "BGC In Progress": "st-prog",
  "BGC Initiated": "st-init",
  "Offer in Progress": "st-offer",
  "Onboarding In Progress": "st-onboard",
  "Onboarding Completed": "st-onboarddone",
  "Position Confirmed": "st-conf",
  "Position Hold": "st-hold",
  "Hold": "st-hold",
  "Yet to Finalize": "st-fin",
  "Dropped": "st-dropped"
};

const STATUS_COLOR = {
  "Started": "#3b5bfd",
  "Received Start Date": "#f59e0b",
  "BGC Completed": "#059669",
  "BGC In Progress": "#0284c7",
  "BGC Initiated": "#64748b",
  "Offer in Progress": "#ec4899",
  "Onboarding In Progress": "#3b5bfd",
  "Onboarding Completed": "#7c3aed",
  "Position Confirmed": "#8b5cf6",
  "Position Hold": "#d97706",
  "Hold": "#d97706",
  "Yet to Finalize": "#94a3b8",
  "Dropped": "#ef4444"
};

const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarBg = (name) => {
  const colors = [
    'linear-gradient(135deg, #3b5bfd, #2563eb)',
    'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    'linear-gradient(135deg, #60a5fa, #1d4ed8)',
    'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    'linear-gradient(135deg, #f59e0b, #b45309)',
    'linear-gradient(135deg, #ec4899, #be185d)',
    'linear-gradient(135deg, #06b6d4, #0e7490)',
    'linear-gradient(135deg, #6366f1, #4338ca)',
  ];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash += name.charCodeAt(i);
  return colors[Math.abs(hash) % colors.length];
};

const parseDate = (d) => {
  if (!d) return 0;
  if (d.includes('/')) {
    const [m, day, y] = d.split('/').map(Number);
    return new Date(y, m - 1, day).getTime();
  }
  const parsed = new Date(d).getTime();
  return isNaN(parsed) ? 0 : parsed;
};

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  const y = dt.getFullYear();
  return `${m}/${day}/${y}`;
};

const SESSION_STATE_KEY = 'onboarding_report_filters';
const SESSION_DATA_KEY = 'onboarding_report_cached_data';

const getSavedSessionState = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const getSavedSessionData = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const OnboardingReport = () => {
  const savedState = useMemo(() => getSavedSessionState(), []);
  const cachedData = useMemo(() => getSavedSessionData(), []);

  const [data, setData] = useState(cachedData || []);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(savedState.search || '');
  const [filter, setFilter] = useState(savedState.filter || 'all');
  const [sortKey, setSortKey] = useState(savedState.sortKey || 'status');
  const [sortDir, setSortDir] = useState(savedState.sortDir !== undefined ? savedState.sortDir : 1);
  const [reportDate, setReportDate] = useState(savedState.reportDate || '');

  // Pagination State for Main Table
  const [mainPage, setMainPage] = useState(savedState.mainPage || 1);
  const [mainPageSize, setMainPageSize] = useState(savedState.mainPageSize || 10);

  // Pagination State for Confirmed Recently Table
  const [recentPage, setRecentPage] = useState(savedState.recentPage || 1);
  const [recentPageSize, setRecentPageSize] = useState(savedState.recentPageSize || 10);

  // Avoid resetting page on initial load if restored from sessionStorage
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setMainPage(1);
  }, [search, filter]);

  // Persist filters, search, sorting, and pagination to sessionStorage
  useEffect(() => {
    try {
      const stateToPersist = {
        search,
        filter,
        sortKey,
        sortDir,
        reportDate,
        mainPage,
        mainPageSize,
        recentPage,
        recentPageSize
      };
      sessionStorage.setItem(SESSION_STATE_KEY, JSON.stringify(stateToPersist));
    } catch (e) {
      console.warn('Could not save onboarding report state to sessionStorage:', e);
    }
  }, [search, filter, sortKey, sortDir, reportDate, mainPage, mainPageSize, recentPage, recentPageSize]);

  // Fetch live original onboarding data from backend
  const fetchLiveData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get(`${BASE_URL}/api/onboarding/queue`, { headers });
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        const liveRows = res.data.data.map(item => {
          let st = 'Onboarding In Progress';
          const rawCode = (item.StatusCode || '').toUpperCase();
          const stageCode = (item.CurrentStageCode || '').toUpperCase();

          if (rawCode === 'COMPLETED' || stageCode === 'ONBOARDING_COMPLETED') {
            st = item.ResultDeploymentId ? 'Started' : 'BGC Completed';
          } else if (rawCode === 'IN_PROGRESS' || stageCode === 'ONBOARDING_INITIATED') {
            st = (item.CompletedSteps && item.CompletedSteps > 0) ? 'BGC In Progress' : 'BGC Initiated';
          } else if (rawCode === 'INITIATED') {
            st = 'BGC Initiated';
          } else if (rawCode === 'HOLD' || rawCode === 'ON_HOLD') {
            st = 'Hold';
          } else if (rawCode === 'DROPPED' || rawCode === 'CANCELLED' || rawCode === 'REJECTED') {
            st = 'Dropped';
          } else if (rawCode === 'OFFER_ACCEPTED' || stageCode === 'OFFER_ACCEPTED' || stageCode === 'READY_FOR_CLIENT') {
            st = 'Offer in Progress';
          }

          let commentText = '';
          if (item.Notes && item.Notes.trim()) {
            commentText = item.Notes.trim();
          } else if (item.JobTitle) {
            commentText = item.JobTitle;
            if (item.ContractorCompanyName) {
              commentText += ` · ${item.ContractorCompanyName}`;
            }
          }

          const candFullName = item.CandidateFullName || 
            item.CandidateName || 
            (item.CandidateFirstName ? `${item.CandidateFirstName} ${item.CandidateLastName || ''}`.trim() : 'Candidate');

          return {
            id: item.OnboardingInstanceId,
            submissionId: item.SubmissionId,
            name: candFullName,
            client: item.ClientCompanyName || item.ClientName || 'Direct',
            end: item.EndClientName || '',
            confirmed: formatDate(item.InitiatedAtUtc || item.CreatedAtUtc || item.DeploymentStartDate),
            status: st,
            comment: commentText,
            jobTitle: item.JobTitle || '',
            employmentType: item.EmploymentType || '',
            flag: rawCode === 'HOLD' || rawCode === 'DROPPED' || stageCode === 'REJECTED',
            totalSteps: item.TotalSteps || 0,
            completedSteps: item.CompletedSteps || 0,
            raw: item
          };
        });

        setData(liveRows);
        try {
          sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(liveRows));
        } catch (e) {
          console.warn('Could not cache live onboarding data in sessionStorage:', e);
        }
      } else {
        setData([]);
      }
    } catch (err) {
      console.error('Error fetching live original onboarding data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load onboarding data.');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveData();
    const today = new Date();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    const y = today.getFullYear();
    setReportDate(`${m}/${d}/${y}`);
  }, [fetchLiveData]);

  // Sorting
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev * -1);
    } else {
      setSortKey(key);
      setSortDir(1);
    }
  };

  // Filter & Search
  const filteredData = useMemo(() => {
    const q = search.toLowerCase().trim();
    return data.filter(r => {
      if (filter === 'flagged' && !(r.flag || r.status === 'Dropped' || r.status === 'Hold')) return false;
      if (filter !== 'all' && filter !== 'flagged' && r.status !== filter) return false;
      if (q) {
        const hay = `${r.name} ${r.client} ${r.end} ${r.comment} ${r.status} ${r.jobTitle}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, search, filter]);

  // Sorted rows
  const sortedData = useMemo(() => {
    const rows = [...filteredData];
    rows.sort((a, b) => {
      let av, bv;
      if (sortKey === 'confirmed') {
        av = parseDate(a.confirmed);
        bv = parseDate(b.confirmed);
      } else {
        av = (a[sortKey] || '').toLowerCase();
        bv = (b[sortKey] || '').toLowerCase();
      }
      return av < bv ? -1 * sortDir : av > bv ? 1 * sortDir : 0;
    });
    return rows;
  }, [filteredData, sortKey, sortDir]);

  // KPI Metrics calculated dynamically from original data
  const total = data.length;
  const startedCount = data.filter(r => r.status === 'Started').length;
  const completedCount = data.filter(r => r.status === 'BGC Completed' || r.status === 'Onboarding Completed').length;
  const inProgCount = data.filter(r => r.status === 'BGC In Progress' || r.status === 'BGC Initiated' || r.status === 'Onboarding In Progress').length;
  const offerCount = data.filter(r => r.status === 'Offer in Progress').length;
  const clientCount = new Set(data.map(r => r.client || 'Direct')).size;

  // Dynamic list of unique clients for header subtitle
  const clientSummaryText = useMemo(() => {
    const names = Array.from(new Set(data.map(r => r.client).filter(Boolean)));
    if (names.length === 0) return 'No clients';
    if (names.length <= 6) return names.join(' · ');
    return `${names.slice(0, 5).join(' · ')} · +${names.length - 5} more`;
  }, [data]);

  // Prior week / recent confirmed calculation
  const confirmedLWRows = useMemo(() => {
    const now = Date.now();
    const fourteenDaysAgo = now - (14 * 24 * 60 * 60 * 1000);
    return data.filter(r => {
      const t = parseDate(r.confirmed);
      return t > 0 && t >= fourteenDaysAgo;
    }).sort((a, b) => parseDate(b.confirmed) - parseDate(a.confirmed));
  }, [data]);

  const confirmedLWCount = confirmedLWRows.length;

  // Paginated Data for Main Table
  const totalMainRows = sortedData.length;
  const totalMainPages = Math.max(1, Math.ceil(totalMainRows / mainPageSize));
  const safeMainPage = Math.min(mainPage, totalMainPages);
  const mainStartIndex = (safeMainPage - 1) * mainPageSize;
  const mainEndIndex = Math.min(mainStartIndex + mainPageSize, totalMainRows);
  const paginatedMainData = useMemo(() => {
    return sortedData.slice(mainStartIndex, mainStartIndex + mainPageSize);
  }, [sortedData, mainStartIndex, mainPageSize]);

  // Paginated Data for Confirmed Recently Table
  const totalRecentRows = confirmedLWRows.length;
  const totalRecentPages = Math.max(1, Math.ceil(totalRecentRows / recentPageSize));
  const safeRecentPage = Math.min(recentPage, totalRecentPages);
  const recentStartIndex = (safeRecentPage - 1) * recentPageSize;
  const recentEndIndex = Math.min(recentStartIndex + recentPageSize, totalRecentRows);
  const paginatedRecentData = useMemo(() => {
    return confirmedLWRows.slice(recentStartIndex, recentStartIndex + recentPageSize);
  }, [confirmedLWRows, recentStartIndex, recentPageSize]);

  // Render pagination bar helper
  const renderPagination = (currentPage, totalPages, totalRows, startIndex, endIndex, pageSize, onPageChange, onPageSizeChange) => {
    if (totalRows === 0) return null;

    return (
      <div className="table-pagination-bar">
        <div className="pagination-left">
          <div className="page-size-selector">
            <span>Show</span>
            <select 
              value={pageSize} 
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                onPageChange(1);
              }}
              className="per-page-select"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span>per page</span>
          </div>
          <span className="pagination-info">
            Showing <strong>{startIndex + 1}</strong>–<strong>{endIndex}</strong> of <strong>{totalRows}</strong> candidates
          </span>
        </div>
        <div className="pagination-controls">
          <button 
            type="button"
            className="page-btn" 
            onClick={() => onPageChange(currentPage - 1)} 
            disabled={currentPage <= 1}
            title="Previous page"
          >
            <ChevronLeft size={15} />
            <span>Prev</span>
          </button>

          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                type="button"
                className={`page-num-btn ${pageNum === currentPage ? 'active' : ''}`}
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum}
              </button>
            ))}
          </div>

          <button 
            type="button"
            className="page-btn" 
            onClick={() => onPageChange(currentPage + 1)} 
            disabled={currentPage >= totalPages}
            title="Next page"
          >
            <span>Next</span>
            <ChevronRight size={15} />
          </button>
        </div>
      </div>
    );
  };

  // Pipeline by Status Breakdown
  const statusOrder = [
    "Started", "Received Start Date", "BGC Completed", "BGC Initiated", 
    "BGC In Progress", "Offer in Progress", "Onboarding In Progress", 
    "Onboarding Completed", "Yet to Finalize", "Hold", "Dropped"
  ];
  const statusCounts = useMemo(() => {
    const counts = {};
    data.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [data]);
  const maxStatusCount = Math.max(1, ...Object.values(statusCounts));

  // Risk items from real data
  const riskItems = useMemo(() => {
    return data.filter(x => x.flag || x.status === 'Dropped' || x.status === 'Hold');
  }, [data]);

  // End-Client Concentration from real data
  const clientConcentration = useMemo(() => {
    const counts = {};
    data.forEach(r => {
      const e = r.end ? r.end : `${r.client} (Direct)`;
      counts[e] = (counts[e] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const max = Math.max(1, ...sorted.map(s => s[1]));
    return { sorted, max };
  }, [data]);

  // CSV Export
  const exportCSV = () => {
    const head = ["Report Date", "Candidate Name", "Client", "End Client", "Confirmed Date", "BGC Status", "Job Title / Role", "Risk Flag"];
    const lines = [head.join(",")];
    filteredData.forEach(r => {
      const cells = [
        reportDate,
        r.name,
        r.client || "Direct",
        r.end || "",
        r.confirmed || "",
        r.status,
        r.jobTitle || r.comment || "",
        r.flag ? "YES" : ""
      ];
      lines.push(cells.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Onboarding_Report_${reportDate.replace(/\//g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Scroll to confirmed section
  const scrollToConfirmed = () => {
    const el = document.getElementById('confirmedSection');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="onboarding-report-wrapper">
      <div className="wrap">
        {/* Modern Header */}
        <header>
          <div className="title-block">
            <div className="eyebrow">
              <span className="live-dot"></span>
              <Layers size={13} />
              HRM · OPERATIONS WORKSTATION
            </div>
            <h1>Onboarding Report</h1>
            <div className="sub">
              <span className="sub-tag">📅 {reportDate}</span>
              <span className="sub-tag">👥 {total} Total Candidates</span>
              <span className="sub-tag">🏢 {clientCount} Active Clients ({clientSummaryText})</span>
            </div>
          </div>
          <div className="actions">
            <button className="rep-btn" onClick={fetchLiveData} title="Reload live database records">
              <RotateCw size={14} className={loading ? 'spin' : ''} />
              Refresh Live Data
            </button>
            <button className="rep-btn" onClick={exportCSV} disabled={data.length === 0}>
              <Download size={14} />
              Export CSV
            </button>
            <button className="rep-btn primary" onClick={() => window.print()}>
              <Printer size={14} />
              Save as PDF
            </button>
          </div>
        </header>

        {/* Loading / Error Banner */}
        {loading && (
          <div style={{ padding: '16px 20px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '14px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', color: '#3b5bfd', fontWeight: '600' }}>
            <RotateCw size={18} className="spin" />
            <span>Syncing live onboarding candidates from database…</span>
          </div>
        )}

        {error && !loading && (
          <div style={{ padding: '16px 20px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '14px', marginBottom: '24px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '600' }}>
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* Ultra-Modern KPI Cards */}
        <div className="kpis">
          <div className="modern-kpi kpi-blue">
            <div className="kpi-top-row">
              <span className="lbl">Total Candidates</span>
              <div className="kpi-icon-badge" style={{ background: '#eff6ff', color: '#2563eb' }}>
                <Users size={16} />
              </div>
            </div>
            <div className="num">{total}</div>
            <div className="kpi-sub-lbl">Active in system</div>
          </div>

          <div className="modern-kpi kpi-purple">
            <div className="kpi-top-row">
              <span className="lbl">Clients Active</span>
              <div className="kpi-icon-badge" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                <Building2 size={16} />
              </div>
            </div>
            <div className="num">{clientCount}</div>
            <div className="kpi-sub-lbl">{clientSummaryText.length > 22 ? `${clientSummaryText.slice(0, 22)}…` : clientSummaryText}</div>
          </div>

          <div 
            className="modern-kpi kpi-cyan" 
            role="button" 
            tabIndex="0" 
            style={{ cursor: 'pointer' }}
            onClick={scrollToConfirmed}
            title="Click to jump to recently confirmed section"
          >
            <div className="kpi-top-row">
              <span className="lbl">Confirmed Recently</span>
              <div className="kpi-icon-badge" style={{ background: '#ecfeff', color: '#0891b2' }}>
                <Calendar size={16} />
              </div>
            </div>
            <div className="num" style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              {confirmedLWCount}
              <span style={{ fontSize: '12px', color: '#0891b2', fontWeight: '700' }}>↓ view</span>
            </div>
            <div className="kpi-sub-lbl">Past 14 days</div>
          </div>

          <div className="modern-kpi kpi-emerald">
            <div className="kpi-top-row">
              <span className="lbl">Started</span>
              <div className="kpi-icon-badge" style={{ background: '#ecfdf5', color: '#059669' }}>
                <Rocket size={16} />
              </div>
            </div>
            <div className="num" style={{ color: '#059669' }}>{startedCount}</div>
            <div className="kpi-sub-lbl">Deployed & active</div>
          </div>

          <div className="modern-kpi kpi-green">
            <div className="kpi-top-row">
              <span className="lbl">BGC Completed</span>
              <div className="kpi-icon-badge" style={{ background: '#eff6ff', color: '#3b5bfd' }}>
                <ShieldCheck size={16} />
              </div>
            </div>
            <div className="num" style={{ color: '#16a34a' }}>{completedCount}</div>
            <div className="kpi-sub-lbl">Clear & ready</div>
          </div>

          <div className="modern-kpi kpi-amber">
            <div className="kpi-top-row">
              <span className="lbl">BGC In Progress</span>
              <div className="kpi-icon-badge" style={{ background: '#fffbeb', color: '#d97706' }}>
                <Hourglass size={16} />
              </div>
            </div>
            <div className="num" style={{ color: '#d97706' }}>{inProgCount}</div>
            <div className="kpi-sub-lbl">Verification active</div>
          </div>

          <div className="modern-kpi kpi-pink">
            <div className="kpi-top-row">
              <span className="lbl">Offer In Progress</span>
              <div className="kpi-icon-badge" style={{ background: '#fdf2f8', color: '#db2777' }}>
                <Sparkles size={16} />
              </div>
            </div>
            <div className="num" style={{ color: '#db2777' }}>{offerCount}</div>
            <div className="kpi-sub-lbl">Awaiting acceptance</div>
          </div>
        </div>

        {/* Main Grid: Left Table, Right Sidebars */}
        <div className="grid">
          <div>
            {/* Modern Toolbar */}
            <div className="modern-toolbar">
              <div className="search-wrapper">
                <Search size={16} className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search candidate, client, job title, status…" 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button 
                    className="search-clear-btn" 
                    onClick={() => setSearch('')}
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="chips-group">
                <button 
                  type="button" 
                  className={`modern-chip ${filter === 'all' ? 'active' : ''}`} 
                  onClick={() => setFilter('all')}
                >
                  All <span className="chip-count">{total}</span>
                </button>
                <button 
                  type="button" 
                  className={`modern-chip ${filter === 'Started' ? 'active' : ''}`} 
                  onClick={() => setFilter('Started')}
                >
                  Started <span className="chip-count">{startedCount}</span>
                </button>
                <button 
                  type="button" 
                  className={`modern-chip ${filter === 'BGC Completed' ? 'active' : ''}`} 
                  onClick={() => setFilter('BGC Completed')}
                >
                  Completed <span className="chip-count">{completedCount}</span>
                </button>
                <button 
                  type="button" 
                  className={`modern-chip ${filter === 'BGC In Progress' ? 'active' : ''}`} 
                  onClick={() => setFilter('BGC In Progress')}
                >
                  In Progress <span className="chip-count">{inProgCount}</span>
                </button>
                <button 
                  type="button" 
                  className={`modern-chip ${filter === 'Offer in Progress' ? 'active' : ''}`} 
                  onClick={() => setFilter('Offer in Progress')}
                >
                  Offer <span className="chip-count">{offerCount}</span>
                </button>
                <button 
                  type="button" 
                  className={`modern-chip ${filter === 'flagged' ? 'active' : ''}`} 
                  onClick={() => setFilter('flagged')}
                >
                  ⚠ Attention <span className="chip-count">{riskItems.length}</span>
                </button>
              </div>
            </div>

            {/* Modern Table Card */}
            <div className="modern-table-card">
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')}>
                      Candidate <span className="arr">{sortKey === 'name' ? (sortDir > 0 ? '▲' : '▼') : ''}</span>
                    </th>
                    <th onClick={() => handleSort('client')}>
                      Client / End Client <span className="arr">{sortKey === 'client' ? (sortDir > 0 ? '▲' : '▼') : ''}</span>
                    </th>
                    <th onClick={() => handleSort('confirmed')}>
                      Confirmed <span className="arr">{sortKey === 'confirmed' ? (sortDir > 0 ? '▲' : '▼') : ''}</span>
                    </th>
                    <th onClick={() => handleSort('status')}>
                      BGC Status <span className="arr">{sortKey === 'status' ? (sortDir > 0 ? '▲' : '▼') : ''}</span>
                    </th>
                    <th>Comments / Role</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMainData.length > 0 ? (
                    paginatedMainData.map((r, idx) => {
                      const cls = STATUS_CLASS[r.status] || "st-fin";
                      const isDropped = r.status === 'Dropped';
                      const isFlagged = r.flag || isDropped || r.status === 'Hold';
                      const pct = r.totalSteps > 0 ? Math.round((r.completedSteps / r.totalSteps) * 100) : 0;

                      return (
                        <tr key={idx} className={`${isFlagged ? 'flagged' : ''} ${isDropped ? 'dropped' : ''}`}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div 
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '10px',
                                  background: getAvatarBg(r.name),
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '13px',
                                  fontWeight: '700',
                                  letterSpacing: '0.04em',
                                  flexShrink: 0,
                                  boxShadow: '0 2px 5px rgba(0,0,0,0.08)'
                                }}
                              >
                                {getInitials(r.name)}
                              </div>
                              <div>
                                <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '13.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  {r.name}
                                  {r.flag && <span className="flag-dot" title="Attention Flagged"></span>}
                                </div>
                                {r.raw?.CandidateEmail && (
                                  <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                                    {r.raw.CandidateEmail}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <div style={{ fontWeight: '600', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <Building2 size={13} style={{ color: '#3b5bfd' }} />
                                {r.client || 'Direct'}
                              </div>
                              {r.end ? (
                                <div style={{ fontSize: '11.5px', color: '#475569', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span style={{ color: '#cbd5e1' }}>↳</span>
                                  <span style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px', fontWeight: '500' }}>
                                    {r.end}
                                  </span>
                                </div>
                              ) : (
                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                  ↳ Direct Placement
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#334155', fontWeight: '500' }}>
                              <Calendar size={13} style={{ color: '#94a3b8' }} />
                              {r.confirmed || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Pending</span>}
                            </div>
                          </td>
                          <td>
                            <span className={`pill ${cls}`}>{r.status}</span>
                          </td>
                          <td>
                            <div>
                              <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px' }}>
                                {r.jobTitle || r.comment || '—'}
                              </div>
                              {r.totalSteps > 0 && (
                                <div style={{ marginTop: '5px', maxWidth: '180px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b', marginBottom: '3px' }}>
                                    <span>Steps</span>
                                    <span style={{ fontWeight: '700', color: '#3b5bfd' }}>
                                      {r.completedSteps}/{r.totalSteps} ({pct}%)
                                    </span>
                                  </div>
                                  <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                                    <div 
                                      style={{ 
                                        height: '100%', 
                                        width: `${pct}%`,
                                        background: pct === 100 ? '#3b5bfd' : '#3b5bfd',
                                        borderRadius: '4px',
                                        transition: 'width 0.3s ease'
                                      }} 
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                        {loading ? 'Loading candidates…' : 'No onboarding candidates found in the database.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {renderPagination(
                safeMainPage, 
                totalMainPages, 
                totalMainRows, 
                mainStartIndex, 
                mainEndIndex, 
                mainPageSize, 
                setMainPage, 
                setMainPageSize
              )}
            </div>

            <div className="count-note">
              Showing {sortedData.length} of {data.length} original candidates from database
            </div>
          </div>

          {/* Right Side Panel */}
          <aside className="side">
            {/* Pipeline by Status Card */}
            <div className="modern-card">
              <div className="card-header">
                <span className="card-title">
                  <TrendingUp size={15} color="var(--teal-primary)" />
                  Pipeline by Status
                </span>
                <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '600' }}>
                  {total} Total
                </span>
              </div>
              <div>
                {statusOrder.filter(s => statusCounts[s]).map((s, idx) => {
                  const c = statusCounts[s];
                  const pct = Math.round((c / maxStatusCount) * 100);
                  const color = STATUS_COLOR[s] || '#3b5bfd';
                  return (
                    <div key={idx} className="bar-row">
                      <div className="bar-head">
                        <span>{s}</span>
                        <span className="v">{c}</span>
                      </div>
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: `${pct}%`, background: color }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Attention & Drops Card */}
            <div className="modern-card">
              <div className="card-header">
                <span className="card-title" style={{ color: riskItems.length > 0 ? '#dc2626' : '#0f172a' }}>
                  <AlertTriangle size={15} color={riskItems.length > 0 ? '#dc2626' : '#94a3b8'} />
                  Attention &amp; Drops
                </span>
                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: riskItems.length > 0 ? '#fef2f2' : '#f1f5f9', color: riskItems.length > 0 ? '#dc2626' : '#64748b', fontWeight: '700' }}>
                  {riskItems.length}
                </span>
              </div>
              <div>
                {riskItems.length > 0 ? (
                  riskItems.map((x, idx) => {
                    const dropped = x.status === 'Dropped';
                    const hold = x.status === 'Hold';
                    const reason = x.comment || (dropped ? 'Dropped from pipeline' : hold ? 'On hold with client' : 'Attention required');
                    const ico = dropped ? '✕' : hold ? '⏸' : '!';
                    const tagStyle = dropped 
                      ? { color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca' }
                      : hold
                      ? { color: '#d97706', background: '#fffbeb', border: '1px solid #fde68a' }
                      : { color: '#0284c7', background: '#f0f9ff', border: '1px solid #bae6fd' };

                    return (
                      <div key={idx} className="risk-item">
                        <span 
                          className="ico"
                          style={{
                            background: dropped ? '#fef2f2' : hold ? '#fffbeb' : '#f0f9ff',
                            color: dropped ? '#dc2626' : hold ? '#d97706' : '#0284c7'
                          }}
                        >
                          {ico}
                        </span>
                        <div>
                          <div className="rname">
                            {x.name}
                            {(dropped || hold) && (
                              <span 
                                style={{
                                  ...tagStyle,
                                  fontSize: '10px',
                                  fontWeight: '700',
                                  padding: '1px 6px',
                                  borderRadius: '4px',
                                  marginLeft: '6px'
                                }}
                              >
                                {dropped ? 'DROPPED' : 'HOLD'}
                              </span>
                            )}
                          </div>
                          <div className="rwhy">{reason}</div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-risk-state">
                    <CheckCircle2 size={16} color="#3b5bfd" />
                    <span>All candidates on track · No blockers or drops</span>
                  </div>
                )}
              </div>
            </div>

            {/* End-Client Concentration Card */}
            <div className="modern-card">
              <div className="card-header">
                <span className="card-title">
                  <Building2 size={15} color="var(--teal-primary)" />
                  Client Concentration
                </span>
                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>
                  Top Placements
                </span>
              </div>
              <div>
                {clientConcentration.sorted.slice(0, 10).map(([name, c], idx) => {
                  const pct = Math.round((c / clientConcentration.max) * 100);
                  return (
                    <div key={idx} className="bar-row">
                      <div className="bar-head">
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700' }}>#{idx + 1}</span>
                          {name}
                        </span>
                        <span className="v">{c}</span>
                      </div>
                      <div className="bar-track">
                        <div 
                          className="bar-fill" 
                          style={{ 
                            width: `${pct}%`, 
                            background: idx === 0 ? 'linear-gradient(90deg, #3b5bfd, #2563eb)' : 'linear-gradient(90deg, #3b82f6, #0284c7)' 
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>

        {/* Confirmed Recently Section */}
        <div className="modern-table-card" id="confirmedSection" style={{ marginTop: '28px', scrollMarginTop: '20px' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', background: '#f8fafc' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#ecfeff', color: '#0891b2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Calendar size={16} />
              </div>
              <div>
                <strong style={{ fontSize: '14.5px', color: '#0f172a', display: 'block' }}>
                  Confirmed Recently
                </strong>
                <span style={{ color: '#64748b', fontSize: '12px' }}>
                  Candidates confirmed or initiated recently in the live database
                </span>
              </div>
            </div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#0891b2', background: '#ecfeff', padding: '3px 10px', borderRadius: '12px' }}>
              {confirmedLWCount} candidates
            </span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Client / End Client</th>
                <th>Confirmed</th>
                <th>BGC Status</th>
                <th>Comments / Role</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRecentData.length > 0 ? (
                paginatedRecentData.map((r, idx) => {
                  const cls = STATUS_CLASS[r.status] || "st-fin";
                  return (
                    <tr key={idx}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div 
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              background: getAvatarBg(r.name),
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '700',
                              flexShrink: 0
                            }}
                          >
                            {getInitials(r.name)}
                          </div>
                          <div>
                            <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '13px' }}>
                              {r.name}
                            </span>
                            {r.flag && <span className="flag-dot" style={{ marginLeft: '6px' }}></span>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: '600', color: '#0f172a' }}>{r.client || 'Direct'}</div>
                        <div style={{ color: '#64748b', fontSize: '11.5px' }}>↳ {r.end || 'Direct Placement'}</div>
                      </td>
                      <td>
                        <span style={{ fontSize: '12.5px', color: '#334155', fontWeight: '500' }}>{r.confirmed}</span>
                      </td>
                      <td><span className={`pill ${cls}`}>{r.status}</span></td>
                      <td>
                        <span style={{ color: '#475569', fontSize: '12.5px', fontWeight: '500' }}>
                          {r.jobTitle || r.comment || '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" style={{ color: '#94a3b8', padding: '24px 20px', textAlign: 'center' }}>
                    No candidates confirmed in this recent window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {renderPagination(
            safeRecentPage, 
            totalRecentPages, 
            totalRecentRows, 
            recentStartIndex, 
            recentEndIndex, 
            recentPageSize, 
            setRecentPage, 
            setRecentPageSize
          )}
        </div>

        {/* Modern Footer */}
        <footer>
          Generated {reportDate} · Prophecy / Cognifyar Onboarding Operations · Live Database Records
        </footer>
      </div>
    </div>
  );
};

export default OnboardingReport;
